import React from "react";
import { motion } from "framer-motion";
import { Home, Ambulance, Heart } from "lucide-react";

export function JourneySection() {
  const journeyStages = [
    {
      icon: Home,
      title: "At Home",
      description: "Empowering self-management with intuitive symptom tracking and medication reminders.",
      color: "#14b8a6",
      status: "🟢 Phase 1: Live Now (Curalysis)",
      features: [
        "Symptom tracking and monitoring",
        "Medication management",
        "Natural language interface",
        "Personalised health insights"
      ]
    },
    {
      icon: Ambulance,
      title: "During Emergency",
      description: "Providing crucial pre-triage information for healthcare professionals and continuous monitoring for peace of mind.",
      color: "#8B5CF6",
      status: "🟣 Phase 3: In Development",
      features: [
        "Risk assessment display",
        "Automatic escalation triggers",
        "Real-time condition monitoring",
        "Seamless provider communication"
      ]
    },
    {
      icon: Heart,
      title: "After Discharge",
      description: "Assisting with recovery tracking, medication reminders, and recognising early warning signs to support lasting health.",
      color: "#14b8a6",
      status: "🔮 Phase 4: Future Vision",
      features: [
        "Personalised recovery plans",
        "Medication reminders",
        "Warning signs monitoring",
        "Progress tracking"
      ]
    }
  ];

  return (
    <section id="journey" className="py-20 relative z-10 bg-white dark:bg-black">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16 text-black dark:text-white"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Support for Australians, From <span className="text-teal-600 dark:text-teal-400">First Symptom to Final Follow-up</span>
          </h2>
          <p className="text-xl text-gray-800 dark:text-gray-200 max-w-3xl mx-auto">
            Elevita provides comprehensive support throughout your entire healthcare journey, adapting to your unique needs at every stage.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {journeyStages.map((stage, index) => (
            <motion.div
              className="p-8 rounded-xl text-center group text-black dark:text-white"
              style={{
                background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.4) 0%, rgba(15, 23, 42, 0.5) 100%)',
                backdropFilter: 'blur(8px)',
                border: '2px solid',
                borderImage: 'linear-gradient(135deg, rgba(20, 184, 166, 0.3), rgba(168, 85, 247, 0.3)) 1',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
              }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              <div 
                className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                style={{ 
                  backgroundColor: `${stage.color}20`,
                  filter: 'drop-shadow(0 0 20px rgba(20, 184, 166, 0.4)) drop-shadow(0 0 30px rgba(168, 85, 247, 0.3))',
                  background: `radial-gradient(circle, ${stage.color}20, transparent)`
                }}
              >
                <stage.icon size={40} style={{ color: stage.color }} />
              </div>
              
              <h3 className="text-2xl font-bold mb-2 text-black dark:text-white">
                {stage.title}
              </h3>
              
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-4 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                {stage.status}
              </div>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                {stage.description}
              </p>
              
              <div className="space-y-3">
                {stage.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-center justify-center space-x-2">
                    <div 
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: stage.color }}
                    />
                    <span className="text-gray-600 dark:text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="text-2xl font-bold mb-8 text-center text-teal-600 dark:text-teal-200">
            Your Complete Healthcare Journey with Elevita
          </h3>
          
          <div className="max-w-5xl mx-auto bg-white dark:bg-black bg-opacity-40 p-8 rounded-2xl border border-gray-700">
            <svg viewBox="0 0 1000 500" className="w-full h-80 mb-6">
              <motion.g
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 1 }}
              >
                {/* Background grid for visual structure */}
                <defs>
                  <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                    <path d="M 50 0 L 0 0 0 50" fill="none" className="stroke-[#C1FFF6] dark:stroke-[#00352D]" strokeWidth="1" opacity={0.1}/>
                  </pattern>
                </defs>
                <rect width="1000" height="500" fill="url(#grid)" />
                
                {/* Journey flow path */}
                <motion.path
                  d="M100,250 C200,250 200,150 300,150 L450,150 C550,150 550,250 650,250 L800,250 C850,250 850,200 900,200"
                  stroke="#14b8a6"
                  strokeWidth="3"
                  fill="none"
                  strokeDasharray="10,5"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  opacity={0.5}
                  transition={{ duration: 2.5, delay: 1.2 }}
                />
                
                {/* Stage containers with enhanced visuals */}
                {journeyStages.map((stage, index) => {
                  const x = 150 + (index * 300);
                  const y = index === 1 ? 150 : 250;
                  
                  return (
                    <motion.g 
                      key={stage.title}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 1.5 + index * 0.3 }}
                    >
                      {/* Stage background */}
                      <rect 
                        x={x - 100} 
                        y={y - 80} 
                        width="200" 
                        height="160" 
                        rx="10"
                        fill={stage.color}
                        opacity="0.6"
                      />
                      
                      {/* Stage icon container */}
                      <circle cx={x} cy={y - 40} r="30" fill={stage.color} opacity="0.3" />
                      <circle cx={x} cy={y - 40} r="25" fill={stage.color} opacity="0.6" />
                      
                      {/* Stage title */}
                      <text x={x} y={y + 10} textAnchor="middle" className="fill-black dark:fill-white" fontSize="18" fontWeight="bold">
                        {stage.title}
                      </text>
                      
                      {/* Feature dots */}
                      {stage.features.slice(0, 3).map((_, fIndex) => (
                        <motion.circle
                          key={fIndex}
                          cx={x - 60 + (fIndex * 60)}
                          cy={y + 40}
                          r="3"
                          className="fill-black dark:fill-white"
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: 2 + index * 0.3 + fIndex * 0.1 }}
                        />
                      ))}
                      
                      {/* Animated pulse effect */}
                      <motion.circle
                        cx={x}
                        cy={y - 40}
                        r="25"
                        className="fill-white dark:fill-black"
                        stroke={stage.color}
                        strokeWidth="2"
                        opacity="0"
                        animate={{
                          r: [25, 35, 25],
                          opacity: [0.6, 0, 0.6]
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          delay: index * 0.5
                        }}
                      />
                    </motion.g>
                  );
                })}
                
                {/* Patient icon moving through journey */}
                <motion.g
                  initial={{ x: 100, y: 250 }}
                  animate={{ 
                    x: [100, 300, 650, 900],
                    y: [250, 150, 250, 200]
                  }}
                  transition={{
                    duration: 6,
                    times: [0, 0.33, 0.66, 1],
                    repeat: Infinity,
                    repeatDelay: 2
                  }}
                >
                  <circle r="8" fill="#14b8a6" />
                  <circle r="12" fill="#14b8a6" opacity="0.3" />
                </motion.g>
                
                {/* Start and end labels */}
                <text x="30" y="360" className="fill-black dark:fill-white font-bold" fontSize="20">First Symptom</text>
                <text x="750" y="360" className="fill-black dark:fill-white font-bold" fontSize="20">Full Recovery</text>
                <text x="350" y="360" className="fill-black dark:fill-white font-bold" fontSize="20">All the way through to..</text>
              </motion.g>
            </svg>
            
            <div className="grid md:grid-cols-3 gap-4 text-center">
              <div className="p-4">
                <p className="text-sm text-[#14b8a6] font-semibold mb-2">24/7 Support</p>
                <p className="text-xs text-black dark:text-[#B0B0B0]">Always available when you need us</p>
              </div>
              <div className="p-4">
                <p className="text-sm text-[#10B981] font-semibold mb-2">Seamless Handover</p>
                <p className="text-xs text-black dark:text-[#B0B0B0]">Smooth transitions between care stages</p>
              </div>
              <div className="p-4">
                <p className="text-sm text-[#F59E0B] font-semibold mb-2">Australian Healthcare</p>
                <p className="text-xs text-black dark:text-[#B0B0B0]">Tailored to our healthcare system</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}